import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { ShieldCheck, ArrowRight, CheckCircle2, Lock, Scale, Phone, PhoneOff, Loader2 } from 'lucide-react';
import useLang from '../hooks/useLang';

const translations = {
  es: {
    badge: 'Auditoría Forense | Cumplimiento Normativo | Certificación ENS',
    badgeCall: 'Auditoría IA — Llamada Directa',
    badgeCallConnecting: 'Conectando con Carlos, Auditor IA...',
    badgeCallActive: 'Colgar llamada Auditoría IA',
    headline_1: 'Certifícate en el ENS antes de que tu competencia te deje ',
    headline_2: 'fuera de la licitación',
    subtitle: 'Auditoría Full-Stack con validez pericial: del código a las consultorías. Metodología pionera en España para el análisis sistemático de la cadena de suministro de consultorías (Vector 8) con validez judicial.',
    profileQuestion: '¡Toma el control! Hagamos de nuestros datos un uso responsable y seguro',
    profilePublicTitle: '1. Canal Privado y Anónimo (Auditorialegal)',
    profilePublicDesc: 'Déjanos tu queja de forma 100% privada sobre empresas que gestionen mal tus datos. Verificamos gratis si incumplen la ley y te damos soporte.',
    profilePrivateTitle: '2. Canal Oficial del Estado (Sede AEPD)',
    profilePrivateDesc: 'Presenta una reclamación administrativa formal ante la Agencia Española de Protección de Datos. Requiere identificación con firma digital o certificado.',
    ctaBtn: 'Quiero mi Diagnóstico Express (Sin coste)',
    ctaSub: 'Diagnóstico inicial de vulnerabilidades externas basado en OSINT, revisado contigo en una sesión de 30 minutos.',
    benefit1: 'Informe Pericial con Validez Judicial',
    benefit2: 'Metodología CCN-STIC Oficial',
    benefit3: 'Resultados en 5 días',
    bgAlt: 'Reunión corporativa de auditoría de cumplimiento'
  },
  en: {
    badge: 'Forensic Audit | Regulatory Compliance | ENS Certification',
    badgeCall: 'AI Audit — Direct Call',
    badgeCallConnecting: 'Connecting to Carlos, AI Auditor...',
    badgeCallActive: 'Hang up AI Audit',
    headline_1: 'Get certified in ENS before your competitors leave you ',
    headline_2: 'out of the tender',
    subtitle: 'Full-Stack audit with expert pericial validity: from code to consultancies. The only audit in Spain that systematically analyzes the consultancies supply chain (Vector 8) with judicial validity.',
    profileQuestion: "Take control! Let's use our data responsibly and safely",
    profilePublicTitle: '1. Private & Anonymous Channel (Auditorialegal)',
    profilePublicDesc: 'Submit your complaint 100% privately about companies mishandling your data. We verify for free if they break the law and support you.',
    profilePrivateTitle: '2. Official State Channel (AEPD Sede)',
    profilePrivateDesc: 'Submit a formal administrative complaint before the Spanish Data Protection Agency. Requires identification with digital signature or certificate.',
    ctaBtn: 'I want my Express Assessment (Free)',
    ctaSub: 'Initial external vulnerability scan based on OSINT, reviewed with you in a 30-minute session.',
    benefit1: 'Expert Witness Report with Judicial Validity',
    benefit2: 'Official CCN-STIC Methodology',
    benefit3: 'Results in 5 days',
    bgAlt: 'Corporate compliance audit meeting'
  }
};

export const Hero = () => {
  const navigate = useNavigate();
  const lang = useLang();
  const t = translations[lang] || translations.es;

  const [vapiCallActive, setVapiCallActive] = useState(false);
  const [vapiConnecting, setVapiConnecting] = useState(false);
  const vapiInstanceRef = useRef(null);

  const startVapiCall = () => {
    if (vapiConnecting || vapiCallActive) return;
    setVapiConnecting(true);
    try {
      if (!vapiInstanceRef.current) {
        if (window.Vapi) {
          vapiInstanceRef.current = new window.Vapi('eb995880-d26d-41fc-81be-01963e06df37');
          
          vapiInstanceRef.current.on('call-start', () => {
            setVapiCallActive(true);
            setVapiConnecting(false);
          });
          
          vapiInstanceRef.current.on('call-end', () => {
            setVapiCallActive(false);
            setVapiConnecting(false);
          });
          
          vapiInstanceRef.current.on('error', (err) => {
            console.error('Vapi WebRTC Error:', err);
            setVapiCallActive(false);
            setVapiConnecting(false);
          });
        } else {
          console.warn('El asistente de voz interactiva se está cargando en segundo plano.');
          setVapiConnecting(false);
          return;
        }
      }
      vapiInstanceRef.current.start('d9653209-6fac-4380-869f-285852c94c96');
    } catch (err) {
      console.error('Error al iniciar llamada de Vapi:', err);
      setVapiConnecting(false);
      setVapiCallActive(false);
    }
  };

  const endVapiCall = () => {
    if (vapiInstanceRef.current) {
      vapiInstanceRef.current.stop();
    }
    setVapiCallActive(false);
    setVapiConnecting(false);
  };

  const toggleVapiCall = () => {
    if (vapiCallActive) {
      endVapiCall();
    } else {
      startVapiCall();
    }
  };

  useEffect(() => {
    return () => {
      if (vapiInstanceRef.current) {
        try {
          vapiInstanceRef.current.stop();
        } catch (e) {}
      }
    };
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="hero" className="relative min-h-screen flex items-center pt-20">
      {/* Background Image with Overlay - Corporate Meeting */}
      <div className="absolute inset-0 z-0">
        <img
          src="/hero-desktop.webp"
          srcSet="/hero-mobile.webp 640w, /hero-desktop.webp 1280w"
          sizes="(max-width: 768px) 640px, 1280px"
          alt={t.bgAlt}
          width={1280}
          height={720}
          fetchPriority="high"
          decoding="sync"
          className="w-full h-full object-cover"
        />

        <div className="absolute inset-0 bg-gradient-overlay opacity-85 lg:opacity-70 transition-opacity duration-300"></div>
      </div>


      {/* Content */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-5xl">
          {/* Interactive Calling Button with Badge Esthetics */}
          <button
            onClick={toggleVapiCall}
            className={`mb-6 border backdrop-blur-sm text-base px-6 py-2 rounded-full flex items-center transition-all duration-300 font-sans font-bold tracking-wide no-transform-transition ${
              vapiCallActive
                ? "bg-red-600/20 text-red-500 border-red-500/35 animate-pulse shadow-[0_0_15px_rgba(239,68,68,0.25)]"
                : vapiConnecting
                ? "bg-amber-500/20 text-amber-500 border-amber-500/35 cursor-wait animate-pulse"
                : "bg-accent/20 text-accent border-accent/30 hover:bg-accent/30 hover:border-accent/40 shadow-[0_4px_12px_rgba(212,175,55,0.1)]"
            }`}
          >
            {vapiConnecting ? (
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            ) : vapiCallActive ? (
              <PhoneOff className="w-5 h-5 mr-2" />
            ) : (
              <Phone className="w-5 h-5 mr-2 text-emerald-400 animate-[bounce_2s_infinite]" />
            )}
            {vapiConnecting
              ? t.badgeCallConnecting
              : vapiCallActive
              ? t.badgeCallActive
              : t.badgeCall}
          </button>

          {/* Main Headline */}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight drop-shadow-[0_4px_12px_rgba(0,0,0,0.5)]">
            {t.headline_1}{' '}
            <span className="text-gradient-gold">{t.headline_2}</span>
          </h1>

          {/* Subtitle */}
          <p className="text-lg sm:text-xl text-white/90 mb-8 max-w-4xl leading-relaxed drop-shadow-[0_2px_8px_rgba(0,0,0,0.4)]">
            {t.subtitle}
          </p>

          {/* Profile Selector */}
          <div className="mb-8">
            <p className="text-accent mb-4 text-xl font-bold drop-shadow-[0_1px_4px_rgba(0,0,0,0.5)] tracking-wide">
              ✦ {t.profileQuestion}
            </p>
            <div className="grid sm:grid-cols-2 gap-4 max-w-3xl">
              <Card
                className="cursor-pointer no-transform-transition border-2 border-accent/30 bg-white/5 backdrop-blur-sm hover:border-accent hover:bg-accent/10 transition-all duration-300 shadow-[0_0_12px_rgba(212,175,55,0.15)] hover:shadow-[0_0_22px_rgba(212,175,55,0.45)]"
                onClick={() => navigate('/canal-denuncias')}>

                <CardContent className="pt-6 pb-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-lg bg-red-500/15 flex items-center justify-center flex-shrink-0">
                      <Lock className="w-6 h-6 text-red-500 animate-[pulse_3s_infinite]" />
                    </div>
                    <div>
                      <p className="font-semibold text-white mb-1">
                        {t.profilePublicTitle}
                      </p>
                      <p className="text-xs text-white/80 leading-relaxed">
                        {t.profilePublicDesc}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card
                className="cursor-pointer no-transform-transition border-2 border-accent/30 bg-white/5 backdrop-blur-sm hover:border-accent hover:bg-accent/10 transition-all duration-300 shadow-[0_0_12px_rgba(212,175,55,0.15)] hover:shadow-[0_0_22px_rgba(212,175,55,0.45)]"
                onClick={() => window.open('https://sedeaepd.gob.es/sede-electronica/home', '_blank', 'noopener,noreferrer')}>

                <CardContent className="pt-6 pb-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-lg bg-red-500/15 flex items-center justify-center flex-shrink-0">
                      <Scale className="w-6 h-6 text-red-500 animate-[pulse_3s_infinite]" />
                    </div>
                    <div>
                      <p className="font-semibold text-white mb-1">
                        {t.profilePrivateTitle}
                      </p>
                      <p className="text-xs text-white/80 leading-relaxed">
                        {t.profilePrivateDesc}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* CTA */}
          <div className="mb-8">
            <Button
              onClick={() => scrollToSection('contacto')}
              size="lg"
              className="bg-accent hover:bg-accent/90 text-accent-foreground font-semibold text-sm sm:text-base md:text-lg px-4 sm:px-6 md:px-8 py-4 sm:py-5 md:py-6 h-auto shadow-gold no-transform-transition w-full sm:w-auto">

              <span className="flex items-center justify-center gap-2">
                <span className="whitespace-normal text-center leading-tight">{t.ctaBtn}</span>
                <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
              </span>
            </Button>
            <p className="text-xs sm:text-sm text-white/85 mt-3 max-w-2xl">
              {t.ctaSub}
            </p>
          </div>

          {/* Quick Benefits */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { id: 'pericial', text: t.benefit1 },
              { id: 'metodologia', text: t.benefit2 },
              { id: 'resultados', text: t.benefit3 }
            ].map((benefit) => (
              <div key={benefit.id} className="flex items-center space-x-2">
                <CheckCircle2 className="w-5 h-5 text-accent flex-shrink-0" />
                <span className="text-sm text-white/90">{benefit.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10">
        <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
          <div className="w-1 h-3 bg-accent rounded-full animate-bounce"></div>
        </div>
      </div>
    </section>
  );
};

export default Hero;